import {Component, inject} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Select} from "@core/components";
import {EmployeeService, GroupService} from '@core/services';
import {ModalService} from '@core/services/modal.service';
import {EmployeeRow, GroupItem, GroupMember} from '@core/types';

@Component({
  selector: 'core-add-group',
  imports: [
    Select,
    ReactiveFormsModule
  ],
  templateUrl: './add-group.html',
  styleUrl: './add-group.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;'
  }
})
export class AddGroup {
  form: FormGroup;
  employees: EmployeeRow[] = [];
  employeeOptions: {label: string; value: string | number}[] = [];
  selectedEmployees: EmployeeRow[] = [];
  isLoadingEmployees = true;
  isSubmitting = false;
  errorMessage = '';
  private previousManagerId: string | number | null = null;
  private modal = inject(ModalService);
  private groupService = inject(GroupService);
  private employeeService = inject(EmployeeService);
  private formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: [''],
      managerId: [null],
      employeeId: [null]
    });
    this.form.get('managerId')?.valueChanges.subscribe(value => this.onManagerChange(value));
    this.loadEmployees();
  }

  async loadEmployees() {
    this.isLoadingEmployees = true;
    const response = await this.employeeService.getEmployees(1, 200, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.isLoadingEmployees = false;
      return;
    }
    this.employees = response.result;
    this.employeeOptions = this.employees.map(emp => ({
      label: emp.name,
      value: emp.id || ''
    }));
    this.isLoadingEmployees = false;
  }

  addSelectedEmployee() {
    if (this.isSubmitting) return;
    const employeeId = this.form.get('employeeId')?.value;
    if (!employeeId) return;
    const employee = this.employees.find(emp => this.sameId(emp.id, employeeId));
    if (!employee) return;
    if (this.sameId(this.form.get('managerId')?.value, employee.id)) return;
    if (this.selectedEmployees.find(emp => this.sameId(emp.id, employee.id))) return;
    this.selectedEmployees = [...this.selectedEmployees, employee];
    this.form.get('employeeId')?.setValue(null);
  }

  removeSelectedEmployee(employee: EmployeeRow) {
    if (this.isSubmitting) return;
    this.selectedEmployees = this.selectedEmployees.filter(emp => !this.sameId(emp.id, employee.id));
    if (this.sameId(this.form.get('managerId')?.value, employee.id)) {
      this.form.get('managerId')?.setValue(null);
    }
  }

  async submit(closeAfter: boolean) {
    if (this.isSubmitting) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.setSubmittingState(true);
    this.errorMessage = '';
    const value = this.form.value;
    const selectedEmployeeIds = this.selectedEmployees
      .map(emp => emp.id)
      .filter((id): id is string | number => id !== undefined && id !== null);
    const managerId = value.managerId as string | number | null;
    const memberIds = managerId && !selectedEmployeeIds.includes(managerId)
      ? [...selectedEmployeeIds, managerId]
      : selectedEmployeeIds;

    const createResponse = await this.groupService.createGroup({
      name: value.name,
      description: value.description
    }, {skipLoader: true});

    if (!createResponse.success || !createResponse.result?.id) {
      this.setSubmittingState(false);
      this.errorMessage = createResponse.message || 'No se pudo crear el grupo.';
      return;
    }

    const groupId = createResponse.result.id;
    const membersResponse = await this.groupService.addMembers(groupId, memberIds, {skipLoader: true});
    if (!membersResponse.success) {
      this.setSubmittingState(false);
      this.errorMessage = membersResponse.message || 'Se creó el grupo, pero no se pudieron asociar los empleados.';
      return;
    }

    if (managerId) {
      const managerResponse = await this.groupService.setManager(groupId, managerId, {skipLoader: true});
      if (!managerResponse.success) {
        this.setSubmittingState(false);
        this.errorMessage = managerResponse.message || 'Se creó el grupo, pero no se pudo asignar el encargado.';
        return;
      }
    }

    const resultGroup = this.buildCreatedGroup(createResponse.result, membersResponse.result, managerId);
    this.setSubmittingState(false);

    if (closeAfter) {
      this.modal.closeTop(resultGroup);
    } else {
      this.form.reset();
      this.selectedEmployees = [];
      this.previousManagerId = null;
    }
  }

  onManagerChange(managerId: string | number | null) {
    if (this.previousManagerId) {
      this.selectedEmployees = this.selectedEmployees.filter(item => !this.sameId(item.id, this.previousManagerId));
    }

    this.previousManagerId = managerId;
    if (!managerId) return;

    const employee = this.employees.find(emp => this.sameId(emp.id, managerId));
    if (!employee) return;

    this.selectedEmployees = [
      employee,
      ...this.selectedEmployees.filter(item => !this.sameId(item.id, employee.id))
    ];
  }

  private setSubmittingState(isSubmitting: boolean) {
    this.isSubmitting = isSubmitting;
    if (isSubmitting) {
      this.form.disable({emitEvent: false});
      return;
    }

    this.form.enable({emitEvent: false});
  }

  private sameId(left: string | number | null | undefined, right: string | number | null | undefined) {
    if (left === null || left === undefined || right === null || right === undefined) {
      return false;
    }

    return String(left) === String(right);
  }

  private buildCreatedGroup(
    createdGroup: GroupItem | undefined,
    membersGroup: GroupItem | undefined,
    managerId: string | number | null
  ): GroupItem | null {
    const baseGroup = membersGroup || createdGroup;
    if (!baseGroup) {
      return null;
    }

    const employees = (baseGroup.employees || []).map(member => ({
      ...member,
      isManager: managerId ? this.sameId(member.id, managerId) : false
    }));
    const manager = managerId
      ? employees.find(member => this.sameId(member.id, managerId)) || null
      : null;

    return {
      ...baseGroup,
      employees,
      manager
    };
  }
}
